import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { CTA, TokenTextAppearance, Text, TextTag } from '@volkswagen-onehub/components-core';
import { Close } from '@volkswagen-onehub/icons-core';
import { useTranslation } from 'react-i18next';
import { minimalScreenWidth } from '@/common/breakpoints';

const rotate = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

export const Dialog = styled.div`
  border: none;
  padding: 0;
  overflow-y: scroll;
  margin: 0 auto;
  width: 100%;
  background: white;
`;

const Backdrop = styled.div`
  position: fixed;
  overflow: hidden;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  opacity: 1;
  z-index: 10;
  animation: ${rotate} 0.2s ease-in;
`;

const DialogWrapper = styled.div`
  display: flex;
  position: fixed;
  z-index: 9999;
  max-width: 1200px;
  max-height: calc(100% - 180px);
  width: 91.66vw;
  overflow-y: scroll;
  justify-content: center;
  align-content: center;
  margin: 0 auto;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -45%) translateY(0px);

  @media screen and ${minimalScreenWidth.m} {
    width: 83.33vw;
  }

  @media screen and ${minimalScreenWidth.l} {
    width: 66.66vw;
  }

  @media screen and ${minimalScreenWidth.xl} {
    width: 58.33vw;
  }

  @media screen and ${minimalScreenWidth.xxl} {
    width: 50vw;
  }

  @media screen and ${minimalScreenWidth.xxxl} {
    width: 41.66vw;
  }
`;

const Headline = styled(Text)`
  margin-inline-end: 24px;
  padding-top: ${({ theme }) => theme.size.static250};
  padding-bottom: ${({ theme }) => theme.size.static150};
  flex-grow: 1;

  @media screen and ${minimalScreenWidth.m} {
    padding-top: ${({ theme }) => theme.size.static350};
  }

  @media screen and ${minimalScreenWidth.xxl} {
    padding-top: ${({ theme }) => theme.size.static400};
    padding-bottom: ${({ theme }) => theme.size.static250};
  }
`;

export const Header = styled.div`
  position: relative;
  top: 0;
  left: 0;
  right: 0;
  background: white;
  display: flex;
  justify-content: space-between;
  z-index: 1;
  padding-inline-start: ${({ theme }) => theme.size.grid001};
`;

export const Main = styled.div`
  padding: ${({ theme }) => theme.size.static250} ${({ theme }) => theme.size.grid001}
    ${({ theme }) => theme.size.static450} ${({ theme }) => theme.size.grid001};
`;

const StyledIcon = styled.div`
  display: flex;
  justify-content: flex-end;
  padding-inline-end: 12px;
  padding-top: 12px;
`;

interface IDialogModal {
  isModalOpen: boolean;
  handleOpenModal: () => void;
  children: React.ReactElement;
  headline?: string;
}

export const DialogModal = ({
  isModalOpen,
  handleOpenModal,
  children,
  headline,
}: IDialogModal): React.ReactElement => {
  const { t } = useTranslation();

  useEffect(() => {
    const close = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isModalOpen) {
        handleOpenModal();
      }
    };

    window.addEventListener('keydown', close);

    return () => window.removeEventListener('keydown', close);
  }, [handleOpenModal, isModalOpen]);

  if (!isModalOpen) {
    return <></>;
  }

  return (
    <>
      <DialogWrapper
        role="dialog"
        aria-hidden="false"
        aria-modal="true"
        aria-labelledby="dialog-title"
        data-testid="dialog-wrapper"
      >
        <Dialog role="document" tabIndex={0}>
          <Header>
            <Headline
              id="dialog-title"
              tag={TextTag.h2}
              bold
              appearance={TokenTextAppearance.headline300}
            >
              {headline}
            </Headline>
            <StyledIcon>
              <CTA
                aria-label={t('equipmentDialog.closeButtonAriaLabel')}
                size="small"
                tag="button"
                emphasis="tertiary"
                icon={<Close />}
                onClick={() => handleOpenModal()}
              />
            </StyledIcon>
          </Header>
          <Main>{children}</Main>
        </Dialog>
      </DialogWrapper>
      <Backdrop onClick={() => handleOpenModal()} data-testid="dialog-backdrop" />
    </>
  );
};
